import { KubernetesCourse } from './types';

export const KUBERNETES_MASTERY_COURSES: KubernetesCourse[] = [
  {
    id: 'kubernetes-administration-cka',
    title: 'Kubernetes Administration (CKA Prep)',
    description: 'Master Kubernetes administration and prepare for the Certified Kubernetes Administrator (CKA) exam.',
    level: 'advanced',
    totalDuration: '12 hours',
    image: '/api/placeholder/400/300',
    tags: ['Kubernetes', 'CKA', 'Administration', 'Certification', 'Cloud Native'],
    prerequisites: ['Docker knowledge', 'Linux command line', 'Networking basics'],
    estimatedHours: 12,
    objectives: [
      'Master Kubernetes cluster administration',
      'Prepare for CKA certification exam',
      'Troubleshoot complex cluster issues',
      'Implement backup and disaster recovery'
    ],
    learningOutcomes: [
      'Install and configure Kubernetes clusters',
      'Manage cluster nodes and components',
      'Configure networking and storage',
      'Implement RBAC and security policies',
      'Troubleshoot cluster and application issues',
      'Perform backup and restore operations',
      'Pass the CKA certification exam'
    ],
    modules: [
      {
        id: 'k8s-cluster-architecture',
        title: 'Cluster Architecture and Installation',
        description: 'Understand Kubernetes architecture and cluster setup',
        content: 'Learn the components that make up a Kubernetes cluster...',
        duration: '2.5 hours',
        difficulty: 'advanced',
        order: 1,
        estimatedTime: '2.5 hours',
        learningObjectives: [
          'Understand control plane components',
          'Install clusters with kubeadm',
          'Configure high availability clusters'
        ],
        codeExamples: [
          {
            title: 'Cluster Initialization',
            code: 'kubeadm init --pod-network-cidr=10.244.0.0/16\nkubectl apply -f flannel.yaml',
            explanation: 'Initialize a Kubernetes cluster with kubeadm and install Flannel CNI.',
            language: 'bash'
          }
        ]
      },
      {
        id: 'k8s-workloads-scheduling',
        title: 'Workloads and Scheduling',
        description: 'Master Pod scheduling and workload management',
        content: 'Learn to deploy and manage various types of workloads...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 2
      },
      {
        id: 'k8s-networking',
        title: 'Cluster Networking',
        description: 'Configure cluster networking and services',
        content: 'Understand Kubernetes networking model and CNI plugins...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 3
      },
      {
        id: 'k8s-storage',
        title: 'Storage Management',
        description: 'Configure persistent storage solutions',
        content: 'Master persistent volumes, storage classes, and CSI drivers...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 4
      },
      {
        id: 'k8s-security',
        title: 'Security and RBAC',
        description: 'Implement cluster security and access control',
        content: 'Configure RBAC, security contexts, and network policies...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 5
      },
      {
        id: 'k8s-troubleshooting',
        title: 'Troubleshooting and Maintenance',
        description: 'Diagnose and fix cluster issues',
        content: 'Learn systematic approaches to troubleshooting...',
        duration: '1.5 hours',
        difficulty: 'expert',
        order: 6
      }
    ],
    type: 'kubernetes'
  },
  {
    id: 'kubernetes-security-best-practices',
    title: 'Kubernetes Security Best Practices',
    description: 'Implement comprehensive security measures for Kubernetes clusters in production environments.',
    level: 'advanced',
    totalDuration: '8 hours',
    image: '/api/placeholder/400/300',
    tags: ['Kubernetes', 'Security', 'DevSecOps', 'Compliance', 'Best Practices'],
    prerequisites: ['Kubernetes basics', 'Security fundamentals', 'DevOps experience'],
    estimatedHours: 8,
    objectives: [
      'Implement defense-in-depth security',
      'Configure security scanning and monitoring',
      'Manage secrets and sensitive data',
      'Ensure compliance with security standards'
    ],
    learningOutcomes: [
      'Harden Kubernetes clusters and workloads',
      'Implement Pod Security Standards',
      'Configure network security policies',
      'Manage secrets with external secret stores',
      'Set up security monitoring and auditing',
      'Scan containers and manifests for vulnerabilities'
    ],
    modules: [
      {
        id: 'k8s-security-hardening',
        title: 'Cluster Hardening',
        description: 'Secure the Kubernetes control plane and nodes',
        content: 'Learn to harden your cluster against common attack vectors...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 1
      },
      {
        id: 'k8s-pod-security',
        title: 'Pod Security Standards',
        description: 'Implement Pod Security Standards and contexts',
        content: 'Configure security contexts and admission controllers...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 2
      },
      {
        id: 'k8s-network-security',
        title: 'Network Security Policies',
        description: 'Control network traffic with policies',
        content: 'Implement micro-segmentation with network policies...',
        duration: '1.5 hours',
        difficulty: 'advanced',
        order: 3
      },
      {
        id: 'k8s-secrets-management',
        title: 'Secrets and Configuration Management',
        description: 'Secure sensitive data and configurations',
        content: 'Integrate with external secret management systems...',
        duration: '1.5 hours',
        difficulty: 'advanced',
        order: 4
      },
      {
        id: 'k8s-security-monitoring',
        title: 'Security Monitoring and Compliance',
        description: 'Monitor security events and ensure compliance',
        content: 'Implement comprehensive security monitoring solutions...',
        duration: '1 hour',
        difficulty: 'advanced',
        order: 5
      }
    ],
    type: 'kubernetes'
  },
  {
    id: 'helm-service-mesh-istio',
    title: 'Helm & Service Mesh with Istio',
    description: 'Master application packaging with Helm and implement service mesh architecture using Istio.',
    level: 'advanced',
    totalDuration: '10 hours',
    image: '/api/placeholder/400/300',
    tags: ['Helm', 'Istio', 'Service Mesh', 'Microservices', 'Cloud Native'],
    prerequisites: ['Kubernetes experience', 'Microservices understanding'],
    estimatedHours: 10,
    objectives: [
      'Package applications with Helm charts',
      'Implement service mesh with Istio',
      'Configure traffic management and security',
      'Monitor microservices communications'
    ],
    learningOutcomes: [
      'Create and manage Helm charts',
      'Deploy complex applications with Helm',
      'Install and configure Istio service mesh',
      'Implement traffic routing and load balancing',
      'Configure mutual TLS and security policies',
      'Monitor and trace microservices'
    ],
    modules: [
      {
        id: 'helm-fundamentals',
        title: 'Helm Package Management',
        description: 'Learn Helm for Kubernetes application packaging',
        content: 'Helm simplifies deployment and management of Kubernetes applications...',
        duration: '2.5 hours',
        difficulty: 'intermediate',
        order: 1
      },
      {
        id: 'helm-charts-advanced',
        title: 'Advanced Helm Charts',
        description: 'Create complex, reusable Helm charts',
        content: 'Master templates, dependencies, and chart best practices...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 2
      },
      {
        id: 'istio-installation',
        title: 'Istio Service Mesh Setup',
        description: 'Install and configure Istio',
        content: 'Set up Istio service mesh for microservices communication...',
        duration: '1.5 hours',
        difficulty: 'advanced',
        order: 3
      },
      {
        id: 'istio-traffic-management',
        title: 'Traffic Management with Istio',
        description: 'Control traffic flow between services',
        content: 'Implement advanced traffic routing and load balancing...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 4
      },
      {
        id: 'istio-security-observability',
        title: 'Security and Observability',
        description: 'Secure and monitor service mesh',
        content: 'Implement mTLS, policies, and comprehensive monitoring...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 5
      }
    ],
    type: 'kubernetes'
  }
];